import { useRouter } from 'next/navigation';
import * as React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Text } from '@mentimeter/ragnar-ui/text';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { StarSolidIcon } from '@mentimeter/ragnar-visuals';
import { SendingIllustration } from '@mentimeter/ragnar-visuals/spotlight';
import { Form } from '@mentimeter/ragnar-form';
import { resetIdentifier } from '../../utils/identifier';
import { gaTrackEvent, useTrack } from '../../utils/tracker';
import { useAppSelector } from '../../redux-hooks';
import {
  getHasCustomTheme,
  getIsQfaActive,
  resultsSharingSelector,
  voteAgainEnabledSelector,
  voteKeySelector,
} from '../../selectors';
import { PageLayout } from '../../ui';
import { SeriesLogo } from '../questions/SeriesLogo';
import SignUpButton from './SignUpButton';
import type { FinishScreenProps } from './FinishScreenProps';
import { SendMeResultsButton } from './SendMeResultsButton';
import { FinishScreenFooter } from './FinishScreenFooter';

interface Rating {
  id: number;
  value: number;
  label: string;
}

const starRatings: Rating[] = [
  { id: 1, value: 1, label: 'Hard to stay focused' },
  { id: 2, value: 2, label: 'Some sparks here and there' },
  { id: 3, value: 3, label: 'Pretty decent' },
  { id: 4, value: 4, label: 'Nicely done!' },
  { id: 5, value: 5, label: 'Spot on!' },
];

export function BeesParticipantRatingExperiment({ qfa }: FinishScreenProps) {
  const router = useRouter();
  const trackVoter = useTrack();

  const [submittedRating, setSubmittedRating] = React.useState(false);
  const [selectedRating, setSelectedRating] = React.useState({
    id: 0,
    value: 0,
    label: 'How engaging was this Menti?',
  });

  React.useEffect(() => {
    gaTrackEvent('event status', 'finished');
  }, []);

  React.useEffect(() => {
    trackVoter({
      event: 'Viewed end screen with feedback',
    });
  }, [trackVoter]);

  const resultsSharing = useAppSelector(resultsSharingSelector);
  const showQfaButton = useAppSelector(getIsQfaActive);
  const voteKey = useAppSelector(voteKeySelector);
  const hasCustomTheme = useAppSelector(getHasCustomTheme);
  const voteAgainEnabled = useAppSelector(voteAgainEnabledSelector);

  const onSeeTheResultsClick = () => {
    gaTrackEvent('button', 'clicked', 'Get presentation results');
    trackVoter({
      event: 'Clicked get results',
    });
  };

  const voteAgain = () => {
    resetIdentifier();

    router.push(`/${voteKey}`);
  };

  return (
    <PageLayout footer={<FinishScreenFooter />}>
      <Box
        width="100%"
        height="100%"
        flex="1 1 auto"
        maxWidth="320px"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <SeriesLogo pt="space20" pb="space10" />
          <Box alignItems="center" width="100%">
            <Box width="100%" mb="space12" alignItems="center">
              <Text
                as="h1"
                color="text"
                fontSize="175"
                lineHeight="snug"
                textAlign="center"
                fontWeight="regular"
              >
                Thanks for joining!
              </Text>
            </Box>
            <Box alignItems="stretch" gap="space2" width="100%">
              <SignUpButton
                text="Sign up for Mentimeter"
                hasCustomTheme={hasCustomTheme}
                variant="primary"
              />
              {showQfaButton && <Box alignItems="stretch">{qfa}</Box>}
              {resultsSharing === 'on' && (
                <SendMeResultsButton
                  text="View the results"
                  onClick={onSeeTheResultsClick}
                />
              )}
              {voteAgainEnabled && (
                <Button
                  size="large"
                  aria-label="Participate again"
                  onClick={voteAgain}
                  py="space4"
                  px="space8"
                >
                  Participate again
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Box alignItems="center">
          {submittedRating ? (
            <Box alignItems="center" width="100%" mb="space6">
              <Box width="100px" my="space4">
                <SendingIllustration />
              </Box>
              <Text color="text" textAlign="center">
                Thank you!
              </Text>
              <Text color="text" textAlign="center">
                Your feedback is on its way.
              </Text>
            </Box>
          ) : (
            <>
              <Text>{selectedRating.label}</Text>
              <Form
                initialValues={{ rating: null }}
                onSubmit={() => {
                  trackVoter({
                    event: 'Clicked submit feedback',
                    properties: {
                      rating: selectedRating.value,
                    },
                  });
                  setSubmittedRating(true);
                }}
              >
                <Box mb="space6" mt="space4" flexDirection="row" gap="space3">
                  {starRatings.map((rating, index) => {
                    const inSelectedRange =
                      selectedRating && index < selectedRating.value;
                    return (
                      <IconButton
                        key={rating.id}
                        variant="tertiary"
                        bg={inSelectedRange ? 'neutral' : 'infoWeakest'}
                        type="button"
                        onClick={() => {
                          trackVoter({
                            event: 'Clicked star',
                            properties: {
                              rating: rating.value,
                            },
                          });
                          setSelectedRating(rating);
                        }}
                      >
                        <StarSolidIcon
                          color={inSelectedRange ? 'primary' : 'neutralWeak'}
                        />
                      </IconButton>
                    );
                  })}
                </Box>
                {selectedRating.value !== 0 && (
                  <Box width="100%" alignItems="center">
                    <Text mb="space4" color="textWeaker">
                      Rate the engagement
                    </Text>
                    <Button variant="primary" type="submit">
                      Submit
                    </Button>
                  </Box>
                )}
              </Form>
            </>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}
